import { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { getAccessToken } from '../lib/api';

/**
 * One socket for the whole app, and one hook to subscribe to a room.
 *
 * Rooms are authorised by the server at subscribe time, so a refused
 * subscription is reported here rather than silently producing an empty feed —
 * a live view that shows nothing looks identical to a live view that is not
 * allowed, and telling those apart matters.
 */

let socket = null;
const roomCounts = new Map();

function ensureSocket() {
  if (socket) return socket;
  socket = io({
    path: '/socket.io',
    transports: ['websocket', 'polling'],
    auth: (cb) => cb({ token: getAccessToken() }),
    reconnectionDelay: 1000,
    reconnectionDelayMax: 8000,
  });
  return socket;
}

export function useConnectionStatus() {
  const [status, setStatus] = useState('connecting');

  useEffect(() => {
    const s = ensureSocket();
    const set = (value) => () => setStatus(value);

    if (s.connected) setStatus('live');
    s.on('connect', set('live'));
    s.on('disconnect', set('offline'));
    s.on('connect_error', set('offline'));

    return () => {
      s.off('connect', set('live'));
      s.off('disconnect', set('offline'));
      s.off('connect_error', set('offline'));
    };
  }, []);

  return status;
}

/**
 * Subscribe to one or more rooms and receive their events.
 *
 * @param {string|string[]|null} rooms  e.g. `vehicle:${id}`, or a list of fleet rooms
 * @param {object} handlers            event name → callback
 * @returns {{ denied: string[] }}     rooms the server refused
 */
export function useLiveRooms(rooms, handlers) {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const [denied, setDenied] = useState([]);

  // Compared by value so a caller can build the list inline without
  // resubscribing on every render.
  const list = Array.isArray(rooms) ? rooms : rooms ? [rooms] : [];
  const key = list.slice().sort().join('|');

  useEffect(() => {
    if (!key) return undefined;
    const wanted = key.split('|');
    const s = ensureSocket();

    const join = () => {
      const refused = [];
      for (const room of wanted) {
        s.emit('subscribe', room, (reply) => {
          if (reply && reply.ok === false) {
            refused.push(room);
            setDenied([...refused]);
          }
        });
      }
    };

    // Reference-count rooms, so two components watching the same vehicle share
    // one subscription and the last to unmount is the one that leaves.
    for (const room of wanted) roomCounts.set(room, (roomCounts.get(room) || 0) + 1);
    if (s.connected) join();
    s.on('connect', join);

    const bound = Object.keys(handlersRef.current || {}).map((event) => {
      const listener = (...args) => handlersRef.current?.[event]?.(...args);
      s.on(event, listener);
      return [event, listener];
    });

    return () => {
      s.off('connect', join);
      for (const [event, listener] of bound) s.off(event, listener);

      for (const room of wanted) {
        const next = (roomCounts.get(room) || 1) - 1;
        if (next <= 0) {
          roomCounts.delete(room);
          s.emit('unsubscribe', room);
        } else {
          roomCounts.set(room, next);
        }
      }
    };
  }, [key]);

  return { denied };
}

/** Single-room convenience — the common case. */
export function useLiveRoom(room, handlers) {
  const { denied } = useLiveRooms(room, handlers);
  return { denied: denied.length > 0 };
}

export function disconnectSocket() {
  if (socket) {
    socket.disconnect();
    socket = null;
    roomCounts.clear();
  }
}
